import { CNS_API_BASE } from "@/config";
import {
  CNSNotification,
  CNSNotificationPage,
  CNSNotificationPreference,
  CNSNotificationUnseenCount,
  JsonPatchOperation,
} from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";

const NOTIFICATIONS_URL = `${CNS_API_BASE}/notifications`;
const PREFERENCES_URL = `${CNS_API_BASE}/notification-preferences`;

interface FetchNotificationsOptions {
  unreadOnly?: boolean;
  page?: number;
  size?: number;
}

/**
 * Fetch the current user's inbox (paginated). Sorted newest-first server-side.
 * Pass {@code unreadOnly} to filter to actionable items.
 */
export async function fetchNotifications(
  options: FetchNotificationsOptions = {},
): Promise<CNSNotificationPage> {
  const params = new URLSearchParams();
  if (options.unreadOnly) params.set("unreadOnly", "true");
  if (options.page != null) params.set("page", String(options.page));
  if (options.size != null) params.set("size", String(options.size));
  const query = params.toString();
  const url = query ? `${NOTIFICATIONS_URL}?${query}` : NOTIFICATIONS_URL;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch notifications");
  return res.json();
}

/** Bell-badge count. Called once on initial mount and on every WebSocket reconnect. */
export async function fetchUnseenCount(): Promise<number> {
  const res = await fetch(`${NOTIFICATIONS_URL}/unseen-count`, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch unseen count");
  const data: CNSNotificationUnseenCount = await res.json();
  return data.count;
}

/**
 * Mark a notification as seen — clears the bell badge for that row only.
 * Server stamps {@code Instant.now()} regardless of the supplied value.
 */
export async function markNotificationSeen(notificationId: string): Promise<CNSNotification> {
  return patchNotification(notificationId, [
    { op: "replace", path: "/seenAt", value: null },
  ]);
}

/**
 * Mark a notification as read (also stamps seen if not already). Used when a
 * row is clicked.
 */
export async function markNotificationRead(notificationId: string): Promise<CNSNotification> {
  return patchNotification(notificationId, [
    { op: "replace", path: "/seenAt", value: null },
    { op: "replace", path: "/readAt", value: null },
  ]);
}

/** Bulk transition: every unread notification of the current user becomes read. */
export async function markAllNotificationsRead(): Promise<void> {
  const res = await fetch(`${NOTIFICATIONS_URL}/mark-all-read`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to mark all notifications read");
}

export async function fetchNotificationPreferences(): Promise<CNSNotificationPreference[]> {
  const res = await fetch(PREFERENCES_URL, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch notification preferences");
  return res.json();
}

/** Update a single channel preference for a given event slug. */
export async function updateNotificationPreference(
  name: string,
  patch: JsonPatchOperation[],
): Promise<CNSNotificationPreference> {
  const res = await fetch(`${PREFERENCES_URL}/${encodeURIComponent(name)}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json-patch+json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to update notification preference");
  return res.json();
}

async function patchNotification(
  notificationId: string,
  patch: JsonPatchOperation[],
): Promise<CNSNotification> {
  const res = await fetch(`${NOTIFICATIONS_URL}/${encodeURIComponent(notificationId)}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json-patch+json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to update notification");
  return res.json();
}
